// src/App.js
// Systems has been fully removed (see chat) — SaaS Applications, Local
// Applications, and Shared Credentials are now the only resource types.
// SaaS Applications is the new default landing page for both shells.
import React from "react";
import { Routes, Route, Navigate, NavLink } from "react-router-dom";
import { useAuth } from "./hooks/useAuth";
import UsersPage    from "./pages/UsersPage";
import UserDetail   from "./pages/UserDetail";
import ConfigPage   from "./pages/ConfigPage";
import EmailConfigPage from "./pages/EmailConfigPage";
import ReportsPage      from './pages/ReportsPage';
import RecycleBinPage       from "./pages/RecycleBinPage";
import LoginPage, { PadlockIcon, MsLogo } from "./components/LoginPage";
import "./App.css";

import SaasApplicationsDashboard    from "./pages/SaasApplicationsDashboard";
import SaasApplicationsOwnerPortal  from "./pages/SaasApplicationsOwnerPortal";
import SaasApplicationsDetail       from "./pages/SaasApplicationsDetail";
import SaasApplicationsFormPage     from "./pages/SaasApplicationsFormPage";
import SaasApplicationsAuditReview  from "./pages/SaasApplicationsAuditReview";

import LocalApplicationsDashboard   from "./pages/LocalApplicationsDashboard";
import LocalApplicationsOwnerPortal from "./pages/LocalApplicationsOwnerPortal";
import LocalApplicationsDetail      from "./pages/LocalApplicationsDetail";
import LocalApplicationsFormPage    from "./pages/LocalApplicationsFormPage";
import LocalApplicationsAuditReview from "./pages/LocalApplicationsAuditReview";

import SharedCredentialsDashboard   from "./pages/SharedCredentialsDashboard";
import SharedCredentialsOwnerPortal from "./pages/SharedCredentialsOwnerPortal";
import SharedCredentialsDetail      from "./pages/SharedCredentialsDetail";
import SharedCredentialsFormPage    from "./pages/SharedCredentialsFormPage";
import SharedCredentialsAuditReview from "./pages/SharedCredentialsAuditReview";

// SaaS Applications — cloud (hosted elsewhere)
const IconCloud = () => (
  <svg viewBox="0 0 20 20" fill="currentColor" width="16"><path d="M5.5 15a4.5 4.5 0 01-.416-8.982A5.5 5.5 0 0115.5 8.05 3.5 3.5 0 0114.5 15h-9z"/></svg>
);
// Local Applications — desktop/monitor (installed on a device)
const IconDesktop = () => (
  <svg viewBox="0 0 20 20" fill="currentColor" width="16"><path fillRule="evenodd" d="M2 4.5A1.5 1.5 0 013.5 3h13A1.5 1.5 0 0118 4.5v8a1.5 1.5 0 01-1.5 1.5h-4.75l.5 2h1.5a.75.75 0 010 1.5h-7a.75.75 0 010-1.5h1.5l.5-2H3.5A1.5 1.5 0 012 12.5v-8zm1.5 0v8h13v-8h-13z" clipRule="evenodd"/></svg>
);
// Shared Credentials — key
const IconKey = () => (
  <svg viewBox="0 0 20 20" fill="currentColor" width="16"><path fillRule="evenodd" d="M8 7a5 5 0 117.15 4.5l.35.35V13h1.5v1.5H18V16h-1.5v1.5h-1.5V16h-.5l-2.5-2.5-1 1-1.5-1.5-1 1L7.5 12.5A5 5 0 018 7zm5-1.5a1.5 1.5 0 100 3 1.5 1.5 0 000-3z" clipRule="evenodd"/></svg>
);

export default function App() {
  const { user, loading, logout } = useAuth();

  if (loading) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "100vh" }}>
        <span className="spinner" style={{ width: 32, height: 32 }} />
      </div>
    );
  }

  // Not signed in — show login page (no auto-redirect)
  if (!user) return <LoginPage redirectUri="/" />;

  const { isAdmin } = user;

  // Non-admin shell — My X views only, all other routes redirect there
  if (!isAdmin) {
    return (
      <div className="app-shell">
        <nav className="sidebar">
          <div className="sidebar-logo">
            <PadlockIcon size={28} />
            <span>SUFC System<br/>Governance Portal</span>
          </div>
          <div className="sidebar-nav">
            <div style={{ fontSize:10, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.08em", color:"var(--gray-400)", padding:"12px 16px 4px", userSelect:"none" }}>User Operations</div>
            <NavLink to="/my-saas-applications" className={({ isActive }) => isActive ? "nav-item active" : "nav-item"}>
              <IconCloud /> My SaaS Applications
            </NavLink>
            <NavLink to="/my-local-applications" className={({ isActive }) => isActive ? "nav-item active" : "nav-item"}>
              <IconDesktop /> My Local Applications
            </NavLink>
            <NavLink to="/my-shared-credentials" className={({ isActive }) => isActive ? "nav-item active" : "nav-item"}>
              <IconKey /> My Shared Credentials
            </NavLink>
          </div>
          <div className="sidebar-user">
            <div className="user-avatar">{user.initials}</div>
            <div className="user-info">
              <div className="user-name">{user.name}</div>
              <div className="user-upn">{user.upn}</div>
            </div>
            <button className="btn-ghost" title="Sign out" onClick={logout}>
              <svg viewBox="0 0 20 20" fill="currentColor" width="16">
                <path fillRule="evenodd" d="M3 3a1 1 0 00-1 1v12a1 1 0 001 1h12a1 1 0 001-1V4a1 1 0 00-1-1H3zm10.293 9.293a1 1 0 001.414 1.414l3-3a1 1 0 000-1.414l-3-3a1 1 0 10-1.414 1.414L14.586 9H7a1 1 0 100 2h7.586l-1.293 1.293z" clipRule="evenodd"/>
              </svg>
            </button>
          </div>
        </nav>
        <main className="main-content">
          <Routes>
            <Route path="/my-saas-applications"            element={<SaasApplicationsOwnerPortal user={user} />} />
            <Route path="/owner-saas-application/:id"       element={<SaasApplicationsDetail isAdmin={false} />} />
            <Route path="/saas-applications/:id/review"     element={<SaasApplicationsAuditReview />} />

            <Route path="/my-local-applications"            element={<LocalApplicationsOwnerPortal user={user} />} />
            <Route path="/owner-local-application/:id"       element={<LocalApplicationsDetail isAdmin={false} />} />
            <Route path="/local-applications/:id/review"     element={<LocalApplicationsAuditReview />} />

            <Route path="/my-shared-credentials"            element={<SharedCredentialsOwnerPortal user={user} />} />
            <Route path="/owner-shared-credential/:id"       element={<SharedCredentialsDetail isAdmin={false} />} />
            <Route path="/shared-credentials/:id/review"     element={<SharedCredentialsAuditReview />} />

            <Route path="/"            element={<Navigate to="/my-saas-applications" replace />} />
            <Route path="*"            element={<Navigate to="/my-saas-applications" replace />} />
          </Routes>
        </main>
      </div>
    );
  }

  // Admin shell — full access
  return (
    <div className="app-shell">
      <nav className="sidebar">
        <div className="sidebar-logo">
          <PadlockIcon size={28} />
          <span>SUFC System<br/>Governance Portal</span>
        </div>
        <div className="sidebar-nav">

          {/* USER OPERATIONS */}
          <div style={{ fontSize:10, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.08em", color:"var(--gray-400)", padding:"12px 16px 4px", userSelect:"none" }}>User Operations</div>
          <NavLink to="/my-saas-applications" className={({ isActive }) => isActive ? "nav-item active" : "nav-item"}>
            <IconCloud /> My SaaS Applications
          </NavLink>
          <NavLink to="/my-local-applications" className={({ isActive }) => isActive ? "nav-item active" : "nav-item"}>
            <IconDesktop /> My Local Applications
          </NavLink>
          <NavLink to="/my-shared-credentials" className={({ isActive }) => isActive ? "nav-item active" : "nav-item"}>
            <IconKey /> My Shared Credentials
          </NavLink>

          {/* IT OPERATIONS */}
          <div style={{ fontSize:10, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.08em", color:"var(--gray-400)", padding:"16px 16px 4px", userSelect:"none" }}>IT Operations</div>
          <NavLink to="/saas-applications" className={({ isActive }) => isActive ? "nav-item active" : "nav-item"}>
            <IconCloud /> Manage SaaS Applications
          </NavLink>
          <NavLink to="/local-applications" className={({ isActive }) => isActive ? "nav-item active" : "nav-item"}>
            <IconDesktop /> Manage Local Applications
          </NavLink>
          <NavLink to="/shared-credentials" className={({ isActive }) => isActive ? "nav-item active" : "nav-item"}>
            <IconKey /> Manage Shared Credentials
          </NavLink>
          <NavLink to="/users" className={({ isActive }) => isActive ? "nav-item active" : "nav-item"}>
            <svg viewBox="0 0 20 20" fill="currentColor" width="16"><path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z"/></svg>
            Manage Users
          </NavLink>
          <NavLink to="/reports" className={({ isActive }) => isActive ? "nav-item active" : "nav-item"}>
            <svg viewBox="0 0 20 20" fill="currentColor" width="16"><path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h7a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd"/></svg>
            Reporting
          </NavLink>

          {/* CONFIGURATION */}
          <div style={{ fontSize:10, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.08em", color:"var(--gray-400)", padding:"16px 16px 4px", userSelect:"none" }}>Configuration</div>
          <NavLink to="/recycle-bin" className={({ isActive }) => isActive ? "nav-item active" : "nav-item"}>
            <svg viewBox="0 0 20 20" fill="currentColor" width="16"><path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd"/></svg>
            Recycle Bin
          </NavLink>
          <NavLink to="/config" className={({ isActive }) => isActive ? "nav-item active" : "nav-item"}>
            <svg viewBox="0 0 20 20" fill="currentColor" width="16"><path fillRule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd"/></svg>
            Settings
          </NavLink>
          <NavLink to="/email-config" className={({ isActive }) => isActive ? "nav-item active" : "nav-item"}>
            <svg viewBox="0 0 20 20" fill="currentColor" width="16"><path d="M2.94 4.94A2 2 0 014.5 4h11a2 2 0 011.56.94L10 9.5 2.94 4.94zM2 6.6V14a2 2 0 002 2h12a2 2 0 002-2V6.6l-7.4 4.75a1 1 0 01-1.2 0L2 6.6z"/></svg>
            Emails
          </NavLink>

        </div>
        <div className="sidebar-user">
          <div className="user-avatar">{user.initials}</div>
          <div className="user-info">
            <div className="user-name">{user.name}</div>
            <div className="user-upn">{user.upn}</div>
          </div>
          <button className="btn-ghost" title="Sign out" onClick={logout}>
            <svg viewBox="0 0 20 20" fill="currentColor" width="16">
              <path fillRule="evenodd" d="M3 3a1 1 0 00-1 1v12a1 1 0 001 1h12a1 1 0 001-1V4a1 1 0 00-1-1H3zm10.293 9.293a1 1 0 001.414 1.414l3-3a1 1 0 000-1.414l-3-3a1 1 0 10-1.414 1.414L14.586 9H7a1 1 0 100 2h7.586l-1.293 1.293z" clipRule="evenodd"/>
            </svg>
          </button>
        </div>
      </nav>
      <main className="main-content">
        <Routes>
          <Route path="/users"       element={<UsersPage />} />
          <Route path="/users/:upn"  element={<UserDetail />} />
          <Route path="/config"            element={<ConfigPage />} />
          <Route path="/email-config"      element={<EmailConfigPage />} />
          <Route path="/reports" element={<ReportsPage />} />
          <Route path="/recycle-bin"          element={<RecycleBinPage />} />

          {/* SaaS Applications */}
          <Route path="/"                                element={<Navigate to="/saas-applications" replace />} />
          <Route path="/saas-applications"              element={<SaasApplicationsDashboard />} />
          <Route path="/saas-applications/new"           element={<SaasApplicationsFormPage />} />
          <Route path="/saas-applications/:id"           element={<SaasApplicationsDetail />} />
          <Route path="/saas-applications/:id/edit"      element={<SaasApplicationsFormPage />} />
          <Route path="/saas-applications/:id/review"    element={<SaasApplicationsAuditReview />} />
          <Route path="/my-saas-applications"            element={<SaasApplicationsOwnerPortal user={user} />} />
          <Route path="/owner-saas-application/:id"      element={<SaasApplicationsDetail isAdmin={false} />} />

          {/* Local Applications */}
          <Route path="/local-applications"              element={<LocalApplicationsDashboard />} />
          <Route path="/local-applications/new"           element={<LocalApplicationsFormPage />} />
          <Route path="/local-applications/:id"           element={<LocalApplicationsDetail />} />
          <Route path="/local-applications/:id/edit"      element={<LocalApplicationsFormPage />} />
          <Route path="/local-applications/:id/review"    element={<LocalApplicationsAuditReview />} />
          <Route path="/my-local-applications"            element={<LocalApplicationsOwnerPortal user={user} />} />
          <Route path="/owner-local-application/:id"      element={<LocalApplicationsDetail isAdmin={false} />} />

          {/* Shared Credentials */}
          <Route path="/shared-credentials"              element={<SharedCredentialsDashboard />} />
          <Route path="/shared-credentials/new"           element={<SharedCredentialsFormPage />} />
          <Route path="/shared-credentials/:id"           element={<SharedCredentialsDetail />} />
          <Route path="/shared-credentials/:id/edit"      element={<SharedCredentialsFormPage />} />
          <Route path="/shared-credentials/:id/review"    element={<SharedCredentialsAuditReview />} />
          <Route path="/my-shared-credentials"            element={<SharedCredentialsOwnerPortal user={user} />} />
          <Route path="/owner-shared-credential/:id"      element={<SharedCredentialsDetail isAdmin={false} />} />

          <Route path="*"            element={<Navigate to="/" replace />} />
        </Routes>
      </main>
    </div>
  );
}
